import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OverlayEventDetail } from '@ionic/core/dist/types/utils/overlays-interface';
import { Step1 } from './Steps/Step1';
import { Step2 } from './Steps/Step2';
import { EfficiencyWizard, EfficiencyWizardEnded, EfficiencyConfirming } from '../../components';
import { AssignReasonFormContext, AssignReasonFormContextProps } from './AssignReasonForm.context';
import { AllieModal, AllieSpinner, AllieText } from '../../../components';
import {
  AssignReasonFormProps,
  LastReasonsAssign,
  NOT_APPLICABLE_FAILURE_MODE,
} from './AssignReasonForm.types';
import { Step0 } from './Steps/Step0';
// eslint-disable-next-line max-len
import { EfficiencyQuickConfirmCheck } from '../../components/EfficiencyWizard/EfficiencyQuickConfirmCheck';
import {
  ModalHeader,
  ModalHeaderCloseButton,
  ModalShell,
  StyledSpinnerWrapper,
  WizardWrapper,
} from './AssignReasonForm.styles';
import { StopTypes } from './AssignReasons.enum';
import { normalizeMachineId } from './AssignReasonForm.utils';
import { isTablet } from '../../../common/utils';
import { AllieCloseOutlineIcon } from '../../../styling/icons';

enum FormState {
  Default,
  LoadingConfirm,
  QuickConfirmModal,
  Ended,
}

const getQuickAssignableReason = (reason: LastReasonsAssign): LastReasonsAssign | undefined => {
  const configuredModes = reason.failureModes ?? [];
  if (configuredModes.length === 0) return reason;

  const availableModes = configuredModes.filter(
    (mode) =>
      mode.isActive &&
      (!mode.machineIds?.length ||
        (reason.lastAssignedMachineId != null &&
          mode.machineIds.includes(reason.lastAssignedMachineId)))
  );

  return availableModes.length > 0 ? { ...reason, failureModes: availableModes } : undefined;
};

export const AssignReasonForm: FC<AssignReasonFormProps> = ({
  className,
  editSource,
  labels,
  step1Props,
  step2Props,
  onConfirm,
  onError,
  confirmingLoadingProps,
  wizardEndedProps,
  onCloseModal,
  data,
  isOpen,
  setIsOpen,
  isEdit,
  lastReasonsAssign,
  setMachineSelected,
  trackingEvents,
  loading,
  stopTypeSelectionHandling,
  handleCategorySelection,
  requiredFieldsConfig,
}) => {
  const {
    stopType,
    stopReasonId,
    machineId,
    componentId,
    originalTimeRange,
    timeRange,
    id,
    note,
    failureModeId,
  } = data;
  const modal = useRef<HTMLIonModalElement>(null);
  const prefilledStopRef = useRef<string>();
  const [formState, setFormState] = useState<FormState>(FormState.Default);
  const [wizardPage, setWizardPage] = useState(0);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(!loading);

  const handleWizardPageChange = useCallback((pageIndex: number) => {
    setWizardPage(pageIndex);
  }, []);

  const initialGroupsMotive = useMemo(() => {
    return (
      (stopReasonId &&
        stopType &&
        step2Props.groupsMotive &&
        step2Props.groupsMotive[stopType]?.find((gm) =>
          gm.motives.some((m) => m.value === stopReasonId)
        )) ||
      undefined
    );
  }, [stopReasonId, stopType, step2Props.groupsMotive]);

  const initialFailureMode = useMemo(() => {
    if (failureModeId) return failureModeId;
    if (stopReasonId && step2Props.failureModesByMotive) {
      const modes = step2Props.failureModesByMotive[stopReasonId] ?? [];
      return modes.length === 0 ? NOT_APPLICABLE_FAILURE_MODE : undefined;
    }
    return undefined;
  }, [failureModeId, stopReasonId, step2Props.failureModesByMotive]);

  const [stepData, setStepData] = useState<AssignReasonFormContextProps>({
    step1: {
      typeStop: isEdit ? stopType || StopTypes.unplanned : StopTypes.all,
    },
    step2: {
      groupMotive: initialGroupsMotive ? Number(initialGroupsMotive?.value) : undefined,
      motive: stopReasonId,
      isMachineRelated: initialGroupsMotive?.isMachineRelated,
      machine: normalizeMachineId(machineId),
      component: componentId,
      note,
      failureMode: initialFailureMode,
      resolvedStopType: stopType,
    },
  });

  const [quickReasonSelectData, setQuickReasonSelectData] = useState<LastReasonsAssign>();
  const quickAssignableReasons = useMemo(
    () =>
      lastReasonsAssign?.flatMap((reason) => {
        const assignableReason = getQuickAssignableReason(reason);
        return assignableReason ? [assignableReason] : [];
      }),
    [lastReasonsAssign]
  );

  const { stopTypeLabels } = labels;

  const {
    groupMotive,
    motive,
    isMachineRelated,
    machine,
    component,
    failureMode,
    note: stepNote,
  } = stepData.step2;
  const cfg = requiredFieldsConfig;

  const hasValue = (v: unknown) => v !== undefined;
  const hasText = (v: string | undefined | null) => v != null && v.trim() !== '';

  const isStep1Valid =
    !!cfg &&
    hasValue(groupMotive) &&
    hasValue(motive) &&
    (!(isMachineRelated || cfg.machineRequired) || hasValue(machine)) &&
    (!cfg.componentRequired || hasValue(component)) &&
    (!cfg.failureModeRequired || hasValue(failureMode)) &&
    (!cfg.commentRequired || hasText(stepNote));

  const stepValid = {
    step0: true,
    step1: isStep1Valid,
  };

  useEffect(() => {
    if (loading || !isEdit || !stopReasonId || !initialGroupsMotive) return;
    const prefillKey = `${id ?? 'unknown'}:${stopReasonId}`;
    if (prefilledStopRef.current === prefillKey) return;

    prefilledStopRef.current = prefillKey;
    setStepData((prev) => {
      if (prev.step2.groupMotive !== undefined) return prev;
      return {
        ...prev,
        step2: {
          ...prev.step2,
          groupMotive: Number(initialGroupsMotive.value),
          isMachineRelated: initialGroupsMotive.isMachineRelated,
        },
      };
    });
  }, [id, initialGroupsMotive, isEdit, loading, stopReasonId]);

  useEffect(() => {
    if (!loading) {
      setHasLoadedInitialData(true);
    }
  }, [loading]);

  const handleClickEnded = () => {
    setIsOpen && setIsOpen(false);
    modal.current?.dismiss();
    onCloseModal();
    setFormState(FormState.Default);
    setStepData((prev) => ({
      ...prev,
      step1: { ...prev.step1, typeStop: StopTypes.all },
    }));
  };

  const handleConfirm = () => {
    setFormState(FormState.LoadingConfirm);

    const allGroupsFlat = Object.values(step2Props.groupsMotive ?? {}).flat();
    const matchedGroup = allGroupsFlat.find((gm) =>
      gm.motives?.some((m) => m.value === stepData.step2.motive)
    );
    const motiveGroupName = matchedGroup?.text;
    const motiveName = matchedGroup?.motives?.find((m) => m.value === stepData.step2.motive)?.text;

    const { resolvedStopType, ...step2Rest } = stepData.step2;

    const cleanedStepData = {
      ...stepData,
      step1: {
        ...stepData.step1,
        typeStop: resolvedStopType ?? stepData.step1.typeStop,
      },
      step2: {
        ...step2Rest,
        failureMode:
          step2Rest.failureMode === NOT_APPLICABLE_FAILURE_MODE
            ? undefined
            : step2Rest.failureMode,
      },
    };

    onConfirm({
      ...cleanedStepData,
      timeRange,
      id,
      motiveGroupName,
      motiveName,
      originalTimeRange,
      isEdit,
      assignmentMethod: 'form',
      previousFailureModeId: failureModeId ?? null,
      editSource: editSource ?? null,
    })
      .then(() => {
        setMachineSelected?.(undefined);
        trackingEvents?.lastReasonsModal('new_reason_option_selected');
        handleClickEnded();
      })
      .catch(() => {
        setFormState(FormState.Default);
        onError?.();
      });
  };

  const step0HandleSelect = (stopReason: LastReasonsAssign) => {
    const assignableReason = getQuickAssignableReason(stopReason);
    if (!assignableReason) return;
    setQuickReasonSelectData(assignableReason);
    setFormState(FormState.QuickConfirmModal);
  };

  const handleOnQuickReasonAssignConfirm = (quickNote?: string) => {
    setFormState(FormState.LoadingConfirm);
    const stepQuickData = {
      step1: {
        typeStop: quickReasonSelectData?.stopType ?? StopTypes.planned,
      },
      step2: {
        groupMotive: quickReasonSelectData?.categoryId ?? undefined,
        motive: quickReasonSelectData?.stopReasonId ?? stopReasonId,
        isMachineRelated: !!quickReasonSelectData?.lastAssignedMachineId,
        machine: quickReasonSelectData?.lastAssignedMachineId ?? undefined,
        component: quickReasonSelectData?.lastAssignedComponentId ?? undefined,
        note: quickNote,
        failureMode: quickReasonSelectData?.failureModes?.[0]?.id ?? undefined,
      },
    };

    onConfirm({
      ...stepQuickData,
      timeRange,
      id,
      motiveGroupName: quickReasonSelectData?.categoryValue,
      motiveName: quickReasonSelectData?.reasonValue,
      originalTimeRange,
      isEdit,
      assignmentMethod: 'quick',
    })
      .then(() => {
        setMachineSelected?.(undefined);
        trackingEvents?.lastReasonsModal(
          'last_assigned_reason_selected',
          quickReasonSelectData?.categoryValue,
          quickReasonSelectData?.failureModes?.[0]?.id ?? null,
          quickReasonSelectData?.failureModes?.[0]?.name ?? null
        );
        handleClickEnded();
      })
      .catch(() => {
        setFormState(FormState.Default);
        onError?.();
      });
  };

  const handleMachineSelection = (value: number | undefined) => {
    setMachineSelected?.(value);
  };

  useEffect(() => {
    stopTypeSelectionHandling?.(stepData.step1.typeStop as StopTypes);
  }, [stepData.step1.typeStop]);

  const currentTitle = useMemo(() => {
    if (formState === FormState.QuickConfirmModal) {
      return labels.quickConfirmationStep.title;
    }
    if (formState === FormState.Default) {
      if (isEdit) {
        return wizardPage === 0 ? labels.step1.title : labels.step2.title;
      }
      return wizardPage === 0 ? labels.step0.title : labels.step2.title;
    }
    return '';
  }, [formState, wizardPage, isEdit, labels]);

  const showInitialLoader = loading && !hasLoadedInitialData;
  const showSharedHeader =
    formState !== FormState.LoadingConfirm && formState !== FormState.Ended;

  return (
    <AllieModal
      ref={modal}
      isOpen={isOpen}
      className={className}
      radius="md"
      onDidDismiss={async () => {
        setIsOpen && setIsOpen(false);
        setFormState(FormState.Default);
        if (!isEdit) {
          setStepData((prev) => ({
            ...prev,
            step1: { ...prev.step1, typeStop: StopTypes.all },
          }));
        }
        return Promise.resolve({} as OverlayEventDetail<any>);
      }}
      backdropDismiss={false}
      width={isTablet ? '100%' : '600px'}
      {...(isTablet && {
        breakpoints: [0, 1],
        initialBreakpoint: 1,
        handle: true,
      })}
    >
      <AssignReasonFormContext.Provider value={{ stepData, setStepData, stepValid }}>
        <ModalShell>
          {showSharedHeader && (
            <ModalHeader>
              <AllieText type="h4" color="neutral-90">
                {currentTitle}
              </AllieText>
              <ModalHeaderCloseButton
                fill="clear"
                allieSize="none"
                aria-label={labels.closeButton}
                onclick={handleClickEnded}
              >
                <AllieCloseOutlineIcon color="neutral-60" size="large" />
              </ModalHeaderCloseButton>
            </ModalHeader>
          )}

          {showInitialLoader ? (
            <StyledSpinnerWrapper>
              <AllieSpinner name="circular" color="primary" />
            </StyledSpinnerWrapper>
          ) : (
            <>
            {formState === FormState.Default && (
              <WizardWrapper>
                <EfficiencyWizard
                  labels={{
                    ...labels,
                    nextButton: !isEdit ? labels.chooseAnotherMethodButton : labels.nextButton,
                  }}
                  pages={[
                    !isEdit ? (
                      <Step0
                        key={'step-0'}
                        lastReasonsAssign={quickAssignableReasons}
                        onSelectStopReason={step0HandleSelect}
                        labels={{
                          ...stopTypeLabels,
                          title: labels.step0.subtitle,
                          emptyState: labels.step0.emptyStateText,
                          emptyStateCTA: labels.step0.emptyStateCTA,
                          selectReason: labels.step0.selectReason,
                        }}
                      />
                    ) : null,
                    isEdit ? <Step1 key={'step-1'} {...step1Props} /> : null,
                    <Step2
                      key={'step-2'}
                      {...step2Props}
                      preserveNoMachineSelection={
                        isEdit && normalizeMachineId(machineId) === undefined
                      }
                      labels={{
                        ...step2Props.labels,
                        stopTypeAll: stopTypeLabels.all,
                        stopTypePlanned: stopTypeLabels.planned,
                        stopTypeUnplanned: stopTypeLabels.unplanned,
                        stopTypeFilterSubtitle: labels.step2.stopTypeFilterSubtitle,
                      }}
                      setMachineSelected={handleMachineSelection}
                      handleCategorySelection={handleCategorySelection}
                      requiredFieldsConfig={requiredFieldsConfig}
                    />,
                  ]}
                  onCancel={handleClickEnded}
                  onConfirm={handleConfirm}
                  onCloseHeader={handleClickEnded}
                  onPageChange={handleWizardPageChange}
                  context={AssignReasonFormContext}
                  initialContextWizardState={{ activePageIndex: wizardPage, steps: 2 }}
                  hideNextInPages={isEdit ? [0, 1] : [1, 2]}
                />
              </WizardWrapper>
            )}
            {formState === FormState.QuickConfirmModal && (
              <EfficiencyQuickConfirmCheck
                reasonSelected={quickReasonSelectData}
                onGoPrev={() => setFormState(FormState.Default)}
                onConfirm={handleOnQuickReasonAssignConfirm}
                commentRequired={!!requiredFieldsConfig?.commentRequired}
                labels={{
                  confirm: labels.quickConfirmationStep.saveButton,
                  back: labels.prevButton,
                  subtitle: labels.quickConfirmationStep.subtitle,
                  planned: stopTypeLabels.planned,
                  unplanned: stopTypeLabels.unplanned,
                  note: labels.note,
                  notePlaceholder: labels.notePlaceholder,
                  labelTypeStop: labels.quickConfirmationStep.labelTypeStop,
                  labelMachine: labels.quickConfirmationStep.labelMachine,
                  labelMotive: labels.quickConfirmationStep.labelMotive,
                  labelGroup: labels.quickConfirmationStep.labelGroup,
                  labelFailureMode: labels.quickConfirmationStep.labelFailureMode,
                  labelNotApplicable: labels.quickConfirmationStep.labelNotApplicable,
                }}
              />
            )}
            {formState === FormState.LoadingConfirm && (
              <EfficiencyConfirming {...confirmingLoadingProps} />
            )}
            {formState === FormState.Ended && (
              <EfficiencyWizardEnded {...wizardEndedProps} onClick={handleClickEnded} />
            )}
            </>
          )}
        </ModalShell>
      </AssignReasonFormContext.Provider>
    </AllieModal>
  );
};
